import {GaiaColors, GaiaRed} from '@gaia/colors';
import emojiSprites from '@gaia/icons/emoji.json';
import {
    outlinedIconHeight,
    outlinedIconRaw,
    outlinedIconWidth,
    pinRaw,
    pinWhiteRaw,
} from '@gaia/icons/waypoints';
import {isUnifiedMarkerName, mapMarkerNameToSVG} from '@gaia/icons/waypoints/icons';
import {createSvgBlobUrl} from '@gaia/utils/createSvgBlobUrl';
import type {ControlPointState, ControlPointType} from '@gaia/utils/typing';
import emojiUnicode from 'emoji-unicode';
import memoize from 'lodash/memoize';
import once from 'lodash/once';

import controlPointFactory from './controlPointFactory';

type SpritePosition = {
    sheet_x: number;
    sheet_y: number;
};

// the pixel ratio used by mapbox when drawing images on the map
export const mapboxImagePixelRatio = 2;

// the intrisic size (defined `width` & `height` attrs) of the Gaia Icon SVGs,
// which should all be exactly the same
export const gaiaIconIntrinsicSize = 24; // from SVG source files

// the intrinsic size (w x h) of a single emoji on the sprite sheet;
// this value will be the size of the ImageData resulting from drawing
// that emoji on a canvas
export const emojiIntrinsicSize = 64;

// the size (w x h) to render decorations inside a rendered outlined icon
export const decorationSize = outlinedIconWidth * 0.6;

// the top-left (x,y) coord of a rendered decoration within an outlined icon
// to center the decoration within the icon
const decorationTopLeftOffset = (outlinedIconWidth - decorationSize) / 2;

// the base size of route control points
const controlPointSize = 100;

export const loadImage = memoize(
    (src: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
            const img = document.createElement('img');
            img.crossOrigin = 'anonymous';
            img.src = src;

            const onLoad = () => {
                img.removeEventListener('error', onError);
                URL.revokeObjectURL(src);
                resolve(img);
            };

            const onError = () => {
                img.removeEventListener('load', onLoad);
                reject(`Failed to load image: ${src}`);
            };

            img.addEventListener('load', onLoad, {once: true});
            img.addEventListener('error', onError, {once: true});
        })
);

export const getRedPinImg = once(() => getPinMarkerImg(GaiaRed));

export function getMarkerImg(
    markerType: MarkerType,
    markerDecoration: string,
    markerColor: GaiaColorHex
): Promise<ImageData | HTMLImageElement> {
    switch (markerType) {
        case 'pin':
            return getPinMarkerImg(markerColor);
        case 'outlined-icon':
            return getOutlinedIconMarkerImg(markerDecoration, markerColor);
    }
}

function getPinMarkerImg(markerColor: GaiaColorHex) {
    return loadImage(createSvgBlobUrl(getPinSvg(markerColor)));
}

export function getPinSvg(markerColor: GaiaColorHex) {
    // white pin has its own image
    return markerColor === GaiaColors['White']
        ? pinWhiteRaw
        : pinRaw.replace('{{markerColor}}', markerColor ?? GaiaRed);
}

export async function getOutlinedIconMarkerImg(
    markerDecoration: string,
    markerColor: GaiaColorHex
) {
    const coloredOutlinedIconImg = await loadImage(
        createSvgBlobUrl(outlinedIconRaw.replace('{{markerColor}}', markerColor))
    );

    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.canvas.width = outlinedIconWidth;
    ctx.canvas.height = outlinedIconHeight;
    ctx.drawImage(coloredOutlinedIconImg, 0, 0, outlinedIconWidth, outlinedIconHeight);

    const decorationImgData = await getMarkerDecoration(markerDecoration);

    if (decorationImgData) {
        const scratch = document.createElement('canvas').getContext('2d')!;
        scratch.canvas.width = decorationImgData.width;
        scratch.canvas.height = decorationImgData.height;
        scratch.putImageData(decorationImgData, 0, 0);

        ctx.drawImage(
            scratch.canvas,
            0,
            0,
            decorationSize,
            decorationSize,
            decorationTopLeftOffset,
            decorationTopLeftOffset,
            decorationSize,
            decorationSize
        );
    }

    return ctx.getImageData(0, 0, outlinedIconWidth, outlinedIconHeight);
}

export function getMarkerDecoration(markerDecoration: string): Promise<ImageData | undefined> {
    if (markerDecoration.startsWith('emoji-')) {
        const [, emoji]: (string | undefined)[] = markerDecoration.split('-');
        return getEmojiMarkerImg(emoji ?? '');
    } else if (isUnifiedMarkerName(markerDecoration)) {
        return getGaiaIconMarkerImg(markerDecoration);
    }

    return Promise.resolve(undefined);
}

export const getGaiaIconMarkerImg = memoize(async (markerDecoration: UnifiedMarkerName) => {
    const iconImg = await loadImage(mapMarkerNameToSVG[markerDecoration]);

    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.canvas.width = decorationSize;
    ctx.canvas.height = decorationSize;
    ctx.drawImage(iconImg, 0, 0, decorationSize, decorationSize);

    return ctx.getImageData(0, 0, decorationSize, decorationSize);
});

export const getEmojiMarkerImg = memoize(async (emoji: string): Promise<ImageData | undefined> => {
    const emojiSpriteSheet = await loadImage(`/static/img/emoji-data/apple/sheet_apple_64.webp`);

    const spritePosition = getEmojiSpritePosition(emoji);

    if (!spritePosition) {
        return;
    }

    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.canvas.width = emojiIntrinsicSize;
    ctx.canvas.height = emojiIntrinsicSize;
    ctx.drawImage(
        emojiSpriteSheet,
        // each emoji on the sprite sheet has a 1px margin
        spritePosition.sheet_x * (emojiIntrinsicSize + 1 + 1),
        spritePosition.sheet_y * (emojiIntrinsicSize + 1 + 1),
        emojiIntrinsicSize,
        emojiIntrinsicSize,
        -1, // sprite images are off-center by 1px
        0,
        decorationSize,
        decorationSize
    );

    return ctx.getImageData(0, 0, decorationSize, decorationSize);
});

export function getEmojiSpritePosition(emoji: string): SpritePosition | undefined {
    const codepoints = emojiUnicode(emoji).replace(/ /g, '-').toUpperCase();

    const spritePosition = emojiSprites.find((s) => s.unified === codepoints) as
        | SpritePosition
        | undefined;

    if (spritePosition) {
        return spritePosition;
    }

    // The emoji sprite sheet look up is not a flat dictionary.
    // Skin tone variants are nested under the base emoji code point.
    const [firstCodepoint, secondCodepoint] = codepoints.split('-');

    const baseEmoji = emojiSprites.find((s) => s.unified === firstCodepoint);

    const skinToneVariants: Record<string, SpritePosition | undefined> | undefined =
        baseEmoji?.skin_variations;

    const skinToneVariant = skinToneVariants?.[secondCodepoint];

    if (skinToneVariant) {
        return skinToneVariant;
    }

    return emojiSprites.find((s) => {
        const [firstUnifiedCodepoint] = s.unified.split('-');
        return firstCodepoint === firstUnifiedCodepoint;
    });
}

export async function getControlPointImage(
    type: ControlPointType,
    color: string,
    state: ControlPointState
) {
    const ctx = document.createElement('canvas').getContext('2d')!;

    const controlPointImg = await loadImage(
        createSvgBlobUrl(controlPointFactory(controlPointSize, state, color, type))
    );

    ctx.canvas.width = ctx.canvas.height = controlPointSize;
    ctx.drawImage(controlPointImg, 0, 0);

    return ctx.getImageData(0, 0, controlPointSize, controlPointSize);
}
